import Phaser from 'phaser';
import { Membrane } from './Membrane';
import { Organelle } from './Organelle';
import { Mitochondrion } from './Mitochondrion';
import {
  calculateATPRegeneration,
  calculateMaxATP,
  calculateMitochondriaProduction
} from '../shared/ResourceSimulation';

export interface OrganelleZone {
  id: string;                     // Unique identifier for this zone
  name: string;                   // Human-readable name
  organelleTypes: string[];       // Which organelle types belong in this zone
  minDistanceFromCenter: number;  // Minimum distance from cell center
  maxDistanceFromCenter: number;  // Maximum distance from cell center
  maxOccupancy: number;           // Maximum number of organelles allowed in this zone
  priority: number;               // Higher priority zones get filled first (0-100)
  distributionPattern?: 'random' | 'uniform' | 'clustered'; // How organelles are distributed
  preferredAngles?: {             // Optional: sectors where organelles prefer to be
    min: number;                  // In radians
    max: number;
  }[];
  avoidAngles?: {                 // Optional: sectors where organelles avoid
    min: number;
    max: number;
  }[];
  minDistanceBetweenOrganelles?: number; // Minimum distance between organelles in this zone
}

export class Cell extends Phaser.GameObjects.Container {
  public membrane: Membrane;
  private organelles: Organelle[] = [];
  private atp: number = 20; // Renamed from energy to atp
  private glucose: number = 30; // Add glucose property
  private lipids: number = 5; // Add lipids property
  private maxATP: number = 50; // Base ATP capacity
  private health: number = 100;
  private membraneStage: number = 1; // Track current membrane expansion stage
  private worldSize: number = 100000; // Match the increased world size in GameScene
  private returnToCenterTimer: Phaser.Time.TimerEvent | null = null;
  private baseScale: number = 1; // Base scale to maintain proportions
  private playerID: string = ''; // Add playerID property to identify cell owner
  
  // Resource production tracking
  private accumulatedATP: number = 0;
  private accumulatedGlucose: number = 0;
  private resourceUpdateNeeded: boolean = false;
  private resourceUpdateTimer: number = 0;
  private resourceUpdateInterval: number = 2000; // Increased to 2000ms (2 seconds) to make production more noticeable
  
  // Resource production source tracking
  private naturalATPRegeneration: number = 0;
  private mitochondriaATPProduction: number = 0;
  private mitochondriaGlucoseConsumption: number = 0;
  
  // Physics properties for realistic movement
  private velocity: Phaser.Math.Vector2;
  private acceleration: Phaser.Math.Vector2;
  private drag: number = 0.95; // Drag coefficient (0-1), higher = less drag
  private maxSpeed: number = 10;
  private mass: number = 1;
  
  // Movement tracking
  private lastPosition: Phaser.Math.Vector2;
  private movementSmoothing: number = 0.2; // Lower = smoother movement
  
  // Target-based movement
  private targetPosition: Phaser.Math.Vector2 | null = null;
  private moveForce: number = 0.5; // Force applied when moving toward target
  private arrivalDistance: number = 10; // Distance at which to consider target reached
  
  // Organelle zones
  private organelleZones: OrganelleZone[] = [
    {
      id: 'nuclear',
      name: 'Nuclear Zone',
      organelleTypes: ['nucleus'],
      minDistanceFromCenter: 0,
      maxDistanceFromCenter: 8, // Reduced from 10 to make nucleus smaller
      maxOccupancy: 1,
      priority: 100, // Highest priority
      distributionPattern: 'uniform'
    },
    {
      id: 'perinuclear',
      name: 'Perinuclear Zone',
      organelleTypes: ['mitochondrion', 'er'],
      minDistanceFromCenter: 42, // Adjusted for smaller nucleus (was 45 originally)
      maxDistanceFromCenter: 65,
      maxOccupancy: 20, // Original value
      priority: 80,
      distributionPattern: 'uniform',
      minDistanceBetweenOrganelles: 25, // Keep mitochondria properly spaced
      // Define 'clusters' where mitochondria can be positioned more biologically accurately
      // This creates regions where mitochondria tend to concentrate in eukaryotic cells
      preferredAngles: [
        { min: Math.PI * 0.25, max: Math.PI * 0.75 },  // Bottom-right quadrant (45-135 degrees)
        { min: Math.PI * 1.25, max: Math.PI * 1.75 }   // Top-left quadrant (225-315 degrees)
      ]
    },
    {
      id: 'intermediate',
      name: 'Intermediate Zone',
      organelleTypes: ['golgi', 'lysosome', 'peroxisome'],
      minDistanceFromCenter: 52, // Adjusted for smaller nucleus (was 55 originally)
      maxDistanceFromCenter: 75,
      maxOccupancy: 15, // Original value
      priority: 60,
      distributionPattern: 'clustered',
      minDistanceBetweenOrganelles: 20, // Add minimum spacing between lysosomes
      // Add preferred angles for lysosomes to create clusters, similar to mitochondria
      preferredAngles: [
        { min: Math.PI * 0.5, max: Math.PI * 1.0 },  // Bottom quadrant (90-180 degrees)
        { min: Math.PI * 1.5, max: Math.PI * 2.0 }   // Right quadrant (270-360 degrees)
      ]
    },
    {
      id: 'peripheral',
      name: 'Peripheral Zone',
      organelleTypes: ['endosome', 'vesicle', 'vacuole'],
      minDistanceFromCenter: 70,
      maxDistanceFromCenter: 95, // Returned to original value
      maxOccupancy: 20,
      priority: 40,
      distributionPattern: 'random'
    }
  ];
  
  // Organelle zone occupancy tracking
  private organelleZoneOccupancy: Map<string, number> = new Map();
  
  // Collection of specialized organelles
  private chloroplasts: Organelle[] = [];
  private mitochondria: Organelle[] = [];
  private lysosomes: Organelle[] = [];
  private flagella: Organelle[] = [];
  
  private lowATPWarningText: Phaser.GameObjects.Text | null = null;
  private isLowATPWarningVisible: boolean = false;
  
  // Movement and ATP tracking
  private isMoving: boolean = false;
  private lastMovementTime: number = 0;
  private atpConsumptionRate: number = 0.7; // Increased from 0.5 ATP/sec
  private minATPForMovement: number = 5; // Minimum ATP needed for normal movement
  
  constructor(scene: Phaser.Scene, x: number, y: number, playerID: string = '') {
    super(scene, x, y);
    
    this.playerID = playerID;
    
    // Add cell membrane with full configuration for a mature cell
    this.membrane = new Membrane(scene, 0, 0, 100, {
      showBilayer: true,
      wobbleAmplitude: [0.08, 0.05, 0.03],
      wobbleFrequency: [2, 4, 7],
      wobbleSpeed: 0.01,
      showPhospholipids: false,
      allowBreaches: true, // Default off, can be enabled later
      respondToMovement: true,
      outerColor: 0x4488FF,
      innerColor: 0x3377DD,
      fillColor: 0xAADDFF,
      outerAlpha: 1.0,
      innerAlpha: 0.8,
      fillAlpha: 0.4  // More transparent fill to better see nucleus
    });
    this.add(this.membrane);
    
    // Initialize resource capacities based on membrane stage
    this.updateMaxATP();
    
    this.velocity = new Phaser.Math.Vector2(0, 0);
    this.acceleration = new Phaser.Math.Vector2(0, 0);
    this.lastPosition = new Phaser.Math.Vector2(x, y);
    
    // Optionally add positioning guides for debug purposes
    const showDebugZones = false; // Set to true to visualize the organelle positioning zones
    if (showDebugZones) {
      this.createPositioningGuides();
    }
    
    // Add a debug outline to make the cell more visible
    // const debugOutline = scene.add.graphics();
    // debugOutline.lineStyle(5, 0xff00ff);
    // debugOutline.strokeCircle(0, 0, 110);
    // this.add(debugOutline);
    
    // Make cell interactive but not draggable
    this.setInteractive(new Phaser.Geom.Circle(0, 0, 100), Phaser.Geom.Circle.Contains); // Changed back to 100 from 110
    
    // Add to scene
    scene.add.existing(this);
    
    // Setup click-to-move
    this.setupClickToMove();
    
    // Set depth to ensure cell is above background but below UI
    this.setDepth(100);
    
    // Set scale to maintain proportions
    this.setScale(this.baseScale);
    
    // Listen for resize events to maintain proportions
    this.scene.scale.on('resize', this.handleResize, this);
    
    // Log cell creation for debugging
    console.log('Cell created with membrane:', this.membrane, 'at position:', x, y, 'with depth:', this.depth);
    
    // Initialize zone occupancy tracking
    this.organelleZones.forEach(zone => {
      this.organelleZoneOccupancy.set(zone.id, 0);
    });
  }

  update(delta: number, resources?: { glucose?: number, atp?: number, lipids?: number }): void {
    // Calculate the delta time in seconds
    const deltaSeconds = delta / 1000;
  
    // Update resources if provided - maintaining full precision
    if (resources) {
      // Track changes for network synchronization
      const previousATP = this.atp;
      const previousGlucose = this.glucose;
      
      // Update resources from external source (like player collecting glucose)
      if (resources.atp !== undefined) {
        this.atp = resources.atp;
      }
      
      if (resources.glucose !== undefined) {
        this.glucose = resources.glucose;
      }
      
      // Calculate differences for accumulation
      const atpDiff = this.atp - previousATP;
      const glucoseDiff = this.glucose - previousGlucose;
      
      // Only accumulate if changes were made by external sources (not production)
      if (atpDiff !== 0 || glucoseDiff !== 0) {
        // Only accumulate positive changes from external sources (like collecting resources)
        // We don't want to double-count production that happens in updateResourceProduction
        if (atpDiff > 0) {
          this.accumulatedATP += atpDiff;
          this.resourceUpdateNeeded = true;
        }
        
        if (glucoseDiff > 0) {
          // For glucose, we track increases from collection, not decreases from consumption
          this.accumulatedGlucose -= glucoseDiff; // Negative because consumption is tracked as positive
          this.resourceUpdateNeeded = true;
        }
      }
    }
    
    // Check if the cell is moving by comparing to last position
    const prevIsMoving = this.isMoving;
    this.isMoving = (this.x !== this.lastPosition.x || this.y !== this.lastPosition.y) || 
                    (this.targetPosition !== null);
                    
    if (this.isMoving) {
      this.lastMovementTime = Date.now();
      
      // Consume ATP for movement if we have any
      if (this.atp > 0) {
        const atpCost = this.atpConsumptionRate * deltaSeconds;
        this.atp = Math.max(0, this.atp - atpCost);
        
        // Track the consumption for network synchronization
        this.accumulatedATP -= atpCost;
        this.resourceUpdateNeeded = true;
        
        // Log consumption for debugging
        if (Math.random() < 0.01) { // 1% chance to log to avoid spam
          console.log(`ATP consumed: ${atpCost.toFixed(2)} (rate: ${this.atpConsumptionRate}/sec), remaining: ${this.atp.toFixed(2)}`);
        }
      }
    } else if (prevIsMoving) {
      // We just stopped moving, force a resource update to sync with server
      this.resourceUpdateNeeded = true;
    }
    
    // Handle movement toward target if one exists
    if (this.targetPosition) {
      this.moveTowardTarget();
    }
    
    // Update physics based on delta time
    this.updatePhysics(delta);
    
    // Calculate max ATP based on number of mitochondria first
    this.updateMaxATP();
    
    // Update resource production from organelles like mitochondria
    this.updateResourceProduction(delta);
    
    // Update organelles with cell position and velocity
    for (const organelle of this.organelles) {
      // Update all organelles with resources for mitochondria
      if (organelle.getType() === 'mitochondrion') {
        // Use any type to work around TypeScript limitations
        // The actual Mitochondrion class has the updated method signature
        (organelle as any).update(delta, 0, 0, this.velocity, { 
          glucose: this.glucose, 
          atp: this.atp, 
          maxATP: this.maxATP,
          isOwnCell: true
        });
      } else {
        // Update regular organelles without resource info
        organelle.update(delta, 0, 0, this.velocity);
      }
      
      // Enforce cell boundary for each organelle
      this.enforceOrganelleCellBoundary(organelle);
    }
    
    // Check for overlapping organelles and reposition as needed
    // Reduced frequency to prevent jitter
    if (Math.random() < 0.05) {
      this.checkForOverlappingOrganelles();
    }
    
    // Update membrane
    this.membrane.update(delta);
    
    // Update last position for next frame
    this.lastPosition.set(this.x, this.y);
    
    // Only check boundaries if we're very far from center
    // This allows more freedom of movement while still preventing the cell from getting lost
    const distanceFromCenter = Math.sqrt(this.x * this.x + this.y * this.y);
    if (distanceFromCenter > this.worldSize * 0.4) {
      this.checkBoundaries();
    }
  }
  
  private updatePhysics(delta: number): void {
    // Normalize delta to ensure consistent physics regardless of frame rate
    const normalizedDelta = delta / 16.67; // 60 FPS = 16.67ms per frame
    
    // Store previous velocity for smoothing
    const prevVelocityX = this.velocity.x;
    const prevVelocityY = this.velocity.y;
    
    // Apply acceleration to velocity with smoothing
    this.velocity.x += this.acceleration.x * normalizedDelta;
    this.velocity.y += this.acceleration.y * normalizedDelta;
    
    // Apply moderate velocity dampening to reduce trembling but still allow movement
    // This creates a weighted average between previous and new velocity
    const dampening = 0.4; // Reduced from 0.7 to allow more movement
    this.velocity.x = this.velocity.x * (1 - dampening) + prevVelocityX * dampening;
    this.velocity.y = this.velocity.y * (1 - dampening) + prevVelocityY * dampening;
    
    // Apply drag to slow down movement
    this.velocity.x *= Math.pow(this.drag, normalizedDelta);
    this.velocity.y *= Math.pow(this.drag, normalizedDelta);
    
    // Completely remove random movement when moving
    if (this.velocity.length() < 0.01) {
      // Only add tiny random movement when completely still
      if (Math.random() < 0.002) {
        this.velocity.x += (Math.random() - 0.5) * 0.005;
        this.velocity.y += (Math.random() - 0.5) * 0.005;
      }
    }
    
    // Limit maximum speed
    const currentSpeed = this.velocity.length();
    if (currentSpeed > this.maxSpeed) {
      this.velocity.scale(this.maxSpeed / currentSpeed);
    }
    
    // Zero out very small movements to prevent trembling
    if (Math.abs(this.velocity.x) < 0.02) this.velocity.x = 0; // Reduced from 0.05
    if (Math.abs(this.velocity.y) < 0.02) this.velocity.y = 0; // Reduced from 0.05
    
    // Apply velocity to position - use smooth movement without rounding
    this.x += this.velocity.x * normalizedDelta;
    this.y += this.velocity.y * normalizedDelta;
    
    // Reset acceleration for next frame
    this.acceleration.reset();
    
    // Apply deformation based on velocity and target-based movement
    const speed = this.velocity.length();
    if (speed > 0.1) { // Lower threshold for more responsive deformation
      const angle = Math.atan2(this.velocity.y, this.velocity.x);
      
      // Calculate deformation amount based on speed
      let deformAmount = speed * 0.7; // Increased from 0.5 for more pronounced stretching
      
      // If moving toward a target, enhance the deformation slightly
      if (this.targetPosition) {
        // Calculate direction to target
        const dx = this.targetPosition.x - this.x;
        const dy = this.targetPosition.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Only enhance if we're not too close to the target
        if (distance > this.arrivalDistance * 2) {
          // Calculate how aligned our velocity is with the direction to target
          const targetAngle = Math.atan2(dy, dx);
          const velocityAngle = Math.atan2(this.velocity.y, this.velocity.x);
          
          // Calculate angle difference (how aligned we are with target direction)
          const angleDiff = Math.abs(this.normalizeAngle(targetAngle - velocityAngle));
          
          // If we're moving roughly toward the target, enhance the deformation
          if (angleDiff < Math.PI * 0.25) { // Within 45 degrees
            // Add a moderate enhancement that scales with distance
            const distanceFactor = Math.min(distance / 300, 1.0);
            deformAmount += distanceFactor * 0.8; // Increased from 0.5 for more pronounced enhancement
          }
        }
      }
      
      // Apply the unified deformation
      this.membrane.applyDeformation(deformAmount, angle);
      
      // Also adjust organelle positions based on deformation
      // Use a reduced factor to make organelle movement more subtle
      this.adjustOrganellePositionsForDeformation(deformAmount * 0.3, angle);
    }
  }

  // adds organelles to the cell, including special handling for certain types
  addOrganelle(organelle: Organelle): void {
    // Add to main organelles collection
    this.organelles.push(organelle);
    
    // Add to container (visual)
    this.add(organelle as Phaser.GameObjects.GameObject);
    
    // Add to specialized collections based on type
    const type = organelle.getType();
    if (type === 'mitochondrion') {
      this.mitochondria.push(organelle);
      // Update ATP capacity after adding a mitochondrion
      this.updateMaxATP();
      
      // Make sure the mitochondrion knows about the current resource state
      (organelle as Mitochondrion).updateResourceInfo({
        cellGlucose: this.glucose,
        cellATP: this.atp,
        cellMaxATP: this.maxATP
      });

      // Update zone occupancy for organelle placement restrictions
      this.updateZoneOccupancy();
    } else if (type === 'lysosome') {
      this.lysosomes.push(organelle);
    } else if (type === 'chloroplast') {
      this.chloroplasts.push(organelle);
    } else if (type === 'flagellum') {
      this.flagella.push(organelle);
    }
    
    // Apply logical positioning to the organelle
    this.positionOrganelleLogically(organelle);
    
    // Update zone occupancy
    this.updateZoneOccupancy();
  }

  togglePhospholipids(): void {
    this.membrane.togglePhospholipids();
  }
  
  getEnergy(): number {
    return this.atp;
  }
  
  getATP(): number {
    return this.atp;
  }
  
  /**
   * Set ATP value, capped to the maximum capacity
   */
  setATP(value: number): void {
    this.atp = Math.max(0, Math.min(this.maxATP, value));
  }
  
  /**
   * Get the cell's current health
   */
  public getHealth(): number {
    return this.health;
  }
  
  setHealth(value: number): void {
    this.health = Math.max(0, Math.min(100, value));
  }
  
  // Add methods for breach functionality
  enableBreaches(enabled: boolean = true): void {
    // Enable breaches in membrane configuration
    // Note: This assumes membrane has a way to update its config
    if (this.membrane) {
      // We need to tell TypeScript that this membrane has config property
      (this.membrane as any).config.allowBreaches = enabled;
    }
  }
  
  createBreach(angle: number, size: number = 0.3): void {
    if (this.membrane) {
      this.membrane.createBreach(angle, size);
      
      // Optionally reduce health when a breach happens
      this.health = Math.max(0, this.health - (size * 20));
    }
  }
  
  // Apply a force to the cell (used for movement)
  applyForce(forceX: number, forceY: number): void {
    // F = ma, so a = F/m
    // Apply force smoothing to reduce initial impulse trembling
    const smoothingFactor = 0.6; // Increased from 0.4 to allow more movement
    this.acceleration.x += (forceX / this.mass) * smoothingFactor;
    this.acceleration.y += (forceY / this.mass) * smoothingFactor;
  }
  
  // Set velocity directly (used for joystick controls)
  setVelocity(x: number, y: number): void {
    this.velocity.x = x;
    this.velocity.y = y;
    
    // Cancel any existing target position when directly setting velocity
    this.targetPosition = null;
  }
  
  // Set a target position for the cell to move toward
  setTargetPosition(x: number, y: number, showMarker: boolean = true): void {
    this.targetPosition = new Phaser.Math.Vector2(x, y);
    
    // If not yet moving, or if target has changed drastically, create a visual marker
    if (showMarker) {
      this.createTargetMarker(x, y);
    }
    
    // Reset acceleration to ensure smooth movement to new target
    this.acceleration.reset();
    this.isMoving = true;
    this.lastMovementTime = this.scene.time.now;
  }
  
  // Create a visual marker at the target position
  private createTargetMarker(x: number, y: number): void {
    // Create a circle marker
    const marker = this.scene.add.circle(x, y, 10, 0x00ff00, 0.5);
    
    // Set a data property to identify this as a movement target marker
    marker.setData('type', 'movementMarker');
    
    // Add a pulsing animation
    this.scene.tweens.add({
      targets: marker,
      alpha: 0,
      scale: 2,
      duration: 1000,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        marker.destroy();
      }
    });
    
    // If we're in the MultiplayerGameScene or GameScene, ensure the UI camera ignores this
    if (this.scene.scene.key === 'MultiplayerGameScene' || this.scene.scene.key === 'GameScene') {
      // Use the scene's method to ensure the marker is ignored by the UI camera if it exists
      if (typeof (this.scene as any).ensureIgnoredByUICamera === 'function') {
        (this.scene as any).ensureIgnoredByUICamera(marker);
      } else {
        // Fallback: Try to get the UI camera directly
        const uiCam = this.scene.cameras.getCamera('uiCam');
        if (uiCam) {
          uiCam.ignore(marker);
        }
      }
    }
  }
  
  // Setup click-to-move functionality
  private setupClickToMove(): void {
    // Get the current scene
    const scene = this.scene;
    
    // Ensure scene exists before setting up click handling
    if (!scene) {
      console.warn('Could not set up click-to-move: scene is undefined');
      return;
    }
    
    // In multiplayer, only process clicks for the local player's cell
    const isMultiplayerScene = 'networkService' in this.scene;
    if (isMultiplayerScene) {
      const sceneAny = this.scene as any;
      // Skip click handling for remote cells
      if (this.playerID !== '' && this.playerID !== sceneAny.networkService?.getSocketId()) {
        return;
      }
    }
    
    // Store a reference to the Cell instance for use in event handlers
    const cellInstance = this;
    
    // Set up click event handler
    scene.input.on('pointerdown', function(pointer: Phaser.Input.Pointer) {
      // Handle right click (debug breach creation)
      if (pointer.rightButtonDown()) {
        // If it's a right-click on the cell WITH shift key, create a breach (debug feature)
        const cellBounds = cellInstance.getBounds();
        const isClickOnCell = cellBounds.contains(pointer.worldX, pointer.worldY);
        
        // TODO: remove breaches on click after testing
        if (isClickOnCell && pointer.event.shiftKey) {
          // Calculate angle for breach creation
          const dx = pointer.worldX - cellInstance.x;
          const dy = pointer.worldY - cellInstance.y;
          const angle = Math.atan2(dy, dx);
          
          // Enable breaches if not already enabled
          cellInstance.enableBreaches(true);
          
          // Create a breach at the clicked angle
          cellInstance.createBreach(angle, 0.3);
          
          console.log('DEBUG: Created breach at angle:', angle);
        }
        
        return;
      }
      
      // Handle left click (movement)
      if (pointer.leftButtonDown()) {
        // Use the stored cell instance to call methods
        const isUIInteraction = cellInstance.isPointerOverUI(pointer);
        
        // Only move the cell if not interacting with UI
        if (!isUIInteraction) {
          // Ensure scene is still valid
          if (cellInstance.scene && cellInstance.scene.cameras && cellInstance.scene.cameras.main) {
            // Convert screen coordinates to world coordinates
            const worldPoint = cellInstance.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
            
            // Set the target position - move regardless of whether click is on cell or not
            // This makes movement more responsive and also ensures organelles in the cell
            // don't interfere with movement
            cellInstance.setTargetPosition(worldPoint.x, worldPoint.y);
            
            // We're using a custom camera follow system in GameScene
            // No need to modify camera follow here - it will automatically follow the cell
          }
        }
      }
    });
  }
  
  /**
   * Checks if a pointer is over any UI elements
   * @param pointer The pointer to check
   * @returns True if the pointer is over a UI element, false otherwise
   */
  private isPointerOverUI(pointer: Phaser.Input.Pointer): boolean {
    try {
      // The most comprehensive safety checks
      if (!this.scene || !this.scene.input || !pointer) {
        return false;
      }

      // Safe check for hitTestPointer availability
      if (typeof this.scene.input.hitTestPointer !== 'function') {
        return false;
      }

      // Get hit game objects with extra safety
      const gameObjects = this.scene.input.hitTestPointer(pointer);
      
      // Check if we got a valid array
      if (!gameObjects || !Array.isArray(gameObjects) || gameObjects.length === 0) {
        return false;
      }

      // Process game objects safely
      for (let i = 0; i < gameObjects.length; i++) {
        const obj = gameObjects[i];
        
        // Skip if object is not valid
        if (!obj) {
          continue;
        }
        
        // Skip the cell itself
        if (obj === this) {
          continue;
        }
        
        // Check if it's an interactive element (with thorough null checking)
        if (obj.input && obj.input.enabled) {
          return true;
        }
      }
      
      // No UI elements found
      return false;
    } catch (error) {
      // If anything goes wrong, log it and fail safely
      console.warn('Error in isPointerOverUI:', error);
      return false;
    }
  }
  
  private handleResize(gameSize: Phaser.Structs.Size): void {
    // Maintain the cell's proportions regardless of window size
    // This ensures the cell doesn't get squeezed when the window is resized
    this.setScale(this.baseScale);
  }
  
  private checkBoundaries(): void {
    // Check if cell is too far from center
    const distanceFromCenter = Math.sqrt(this.x * this.x + this.y * this.y);
    const maxAllowedDistance = this.worldSize * 0.45; // 45% of world size
    
    if (distanceFromCenter > maxAllowedDistance && !this.returnToCenterTimer) {
      // Set up a timer to gradually pull the cell back toward center
      this.returnToCenterTimer = this.scene.time.addEvent({
        delay: 50,
        callback: this.gentlyGuideTowardCenter, // Use the gentler method
        callbackScope: this,
        loop: true
      });
    }
  }
  
  private gentlyGuideTowardCenter(): void {
    // Calculate direction to center
    const angle = Math.atan2(this.y, this.x);
    
    // Calculate distance from center
    const distanceFromCenter = Math.sqrt(this.x * this.x + this.y * this.y);
    
    // Calculate how far beyond the allowed boundary we are
    const maxAllowedDistance = this.worldSize * 0.45;
    const excessDistance = distanceFromCenter - maxAllowedDistance;
    
    // Only apply force if we're still beyond the boundary
    if (excessDistance > 0) {
      // Calculate force magnitude - increases with distance beyond boundary
      // Use a quadratic curve for smoother acceleration
      const forceFactor = 0.00001; // Very gentle base force
      const forceMagnitude = forceFactor * excessDistance * excessDistance;
      
      // Apply force toward center - gentler than direct position change
      const forceX = -Math.cos(angle) * forceMagnitude;
      const forceY = -Math.sin(angle) * forceMagnitude;
      
      this.applyForce(forceX, forceY);
      
      // Apply a small amount of drag when beyond boundaries to help slow down
      // This prevents the cell from building up too much momentum
      if (this.velocity.length() > 1) {
        this.velocity.scale(0.99);
      }
    }
    
    // Check if close enough to boundary to stop the timer
    if (distanceFromCenter < this.worldSize * 0.44) { // Slightly inside the boundary
      if (this.returnToCenterTimer) {
        this.returnToCenterTimer.remove();
        this.returnToCenterTimer = null;
      }
    }
  }
  
  // Keep the original moveTowardCenter as a backup or for other uses
  private moveTowardCenter(): void {
    // Calculate direction to center
    const angle = Math.atan2(this.y, this.x);
    
    // Move slightly toward center - use a percentage of distance for smoother movement
    const distanceFromCenter = Math.sqrt(this.x * this.x + this.y * this.y);
    const moveSpeed = distanceFromCenter * 0.01; // 1% of distance per update
    
    this.x -= Math.cos(angle) * moveSpeed;
    this.y -= Math.sin(angle) * moveSpeed;
    
    // Check if close enough to center to stop
    if (distanceFromCenter < this.worldSize * 0.4) {
      if (this.returnToCenterTimer) {
        this.returnToCenterTimer.remove();
        this.returnToCenterTimer = null;
      }
    }
  }
  
  // Helper function to normalize angles to -PI to PI range
  private normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= Math.PI * 2;
    while (angle < -Math.PI) angle += Math.PI * 2;
    return angle;
  }
  
  // Move the cell toward the target position
  private moveTowardTarget(): void {
    if (!this.targetPosition) return;
    
    // Show or hide ATP warning based on threshold (5 ATP), only for own player
    if (this.atp <= 5 && this.playerID != '') {
      // Show low ATP warning
      this.showLowATPWarning();
      
      // If completely out of ATP, stop movement entirely
      if (this.atp <= 0) {
        this.velocity.scale(0.7); // More aggressive slowdown (was 0.9)
        this.targetPosition = null; // Cancel movement completely
        return;
      }
      
      // More aggressive slowdown as ATP gets lower
      // Scale from 0.5 at minATPForMovement to 0.1 at 0 ATP
      const slowdownFactor = 0.5 - (0.4 * (1 - this.atp / this.minATPForMovement));
      
      // Apply progressive slowdown
      if (this.velocity.length() > 0.5) {
        this.velocity.scale(slowdownFactor);
      }
    } else {
      // Hide warning if above the threshold
      this.hideLowATPWarning();
    }
    
    // Calculate direction to target
    const dx = this.targetPosition.x - this.x;
    const dy = this.targetPosition.y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Implement gradual slowdown as we approach the target
    const slowdownDistance = this.arrivalDistance * 8; // Reduced from 10
    
    // Check if we've reached the target
    if (distance < this.arrivalDistance) {
      // Smoothly stop by reducing velocity instead of setting target to null immediately
      this.velocity.scale(0.85); // Reduce velocity by 15% each frame when very close
      
      // Only clear the target when we're very close and moving very slowly
      if (this.velocity.length() < 0.1) {
        this.targetPosition = null;
        // Zero out velocity completely when target is reached
        this.velocity.reset();
      }
      return;
    }
    
    // Calculate direction vector
    const dirX = dx / distance;
    const dirY = dy / distance;
    
    // Apply force toward target, stronger when further away
    let forceMagnitude = Math.min(distance * 0.01, this.moveForce);
    
    // Gradually reduce force as we get closer to the target
    if (distance < slowdownDistance) {
      // Scale force down as we approach the target - smoother curve
      const slowdownFactor = distance / slowdownDistance;
      forceMagnitude *= slowdownFactor * slowdownFactor; // Reduced from cubic to square for more force
    }
    
    // Limit initial force to prevent large impulses that cause trembling
    const maxInitialForce = 0.2; // Increased from 0.1 to allow more movement
    if (this.velocity.length() < 0.5 && forceMagnitude > maxInitialForce) {
      forceMagnitude = maxInitialForce;
    }
    
    // Apply a gradual ramp-up of force at the start of movement
    // This prevents the initial "jerk" that causes trembling
    if (this.velocity.length() < 1.0) {
      // Scale force based on current speed for a smoother start
      const startupFactor = Math.min(this.velocity.length() / 1.0, 1.0);
      forceMagnitude *= 0.5 + (startupFactor * 0.5); // Start at 50% force (increased from 30%)
    }
    
    this.applyForce(dirX * forceMagnitude, dirY * forceMagnitude);
    
    // We'll handle deformation in updatePhysics based on actual velocity
    // This ensures a single, consistent deformation calculation
  }
  
  /**
   * Shows a visual warning when ATP is running low
   */
  private showLowATPWarning(): void {
    if (this.isLowATPWarningVisible) return;
    
    this.isLowATPWarningVisible = true;
    
    // Create warning text if it doesn't exist
    if (!this.lowATPWarningText) {
      this.lowATPWarningText = this.scene.add.text(0, -100, "Low ATP!", {
        fontSize: '16px',
        fontFamily: 'Arial',
        color: '#ff0000',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0.5);
      
      this.add(this.lowATPWarningText);
      
      // Add pulsing animation
      this.scene.tweens.add({
        targets: this.lowATPWarningText,
        alpha: 0.5,
        duration: 500,
        yoyo: true,
        repeat: -1
      });
    } else {
      this.lowATPWarningText.setVisible(true);
    }
  }
  
  /**
   * Hides the low ATP warning
   */
  private hideLowATPWarning(): void {
    if (!this.isLowATPWarningVisible) return;
    
    this.isLowATPWarningVisible = false;
    
    if (this.lowATPWarningText) {
      this.lowATPWarningText.setVisible(false);
    }
  }

  // Add methods for organelle management
  getOrganelles(): Organelle[] {
    return [...this.organelles]; // Return a copy to prevent direct modification
  }

  removeOrganelle(organelle: Organelle): void {
    const index = this.organelles.indexOf(organelle);
    if (index !== -1) {
      this.organelles.splice(index, 1);
      
      // Remove from specialized collections if applicable
      if (organelle.getType() === 'mitochondrion') {
        const mitoIndex = this.mitochondria.indexOf(organelle);
        if (mitoIndex !== -1) {
          this.mitochondria.splice(mitoIndex, 1);
          // Update ATP capacity after removing a mitochondrion
          this.updateMaxATP();
        }
      } else if (organelle.getType() === 'lysosome') {
        const lysoIndex = this.lysosomes.indexOf(organelle);
        if (lysoIndex !== -1) {
          this.lysosomes.splice(lysoIndex, 1);
        }
      } else if (organelle.getType() === 'chloroplast') {
        // ... existing code ...
      } else if (organelle.getType() === 'flagellum') {
        // ... existing code ...
      }
      
      // Destroy the organelle
      organelle.destroy();
      
      // Update zone occupancy
      this.updateZoneOccupancy();
    }
  }

  /**
   * Positions an organelle in a biologically appropriate location based on its type
   * using the zone system for accurate cellular organization
   * @param organelle The organelle to position
   */
  private positionOrganelleLogically(organelle: Organelle): void {
    // Check if the organelle should maintain its position - add this at the top
    // if (organelle.getConfig && typeof organelle.getConfig === 'function') {
    //   const config = organelle.getConfig();
    //   if (config.maintainPosition) {
    //     console.log(`Organelle ${organelle.getType()} (${organelle.getId()}) is configured to maintain position, skipping logical positioning`);
    //     return; // Skip positioning for organelles set to maintain their position
    //   }
    // }

    const type = organelle.getType();
    // TODO: we should use real cell radius here, as it changes when the membrane expands
    const cellRadius = 100; // Approximate radius of the cell
    
    // Find appropriate zones for this organelle type
    const eligibleZones = this.organelleZones.filter(zone => 
      zone.organelleTypes.includes(type)
    ).sort((a, b) => b.priority - a.priority); // Sort by priority (highest first)
    
    if (eligibleZones.length === 0) {
      console.warn(`No defined zone for organelle type: ${type}, using default positioning`);
      // Default random positioning for unknown types
      const randomRadius = Phaser.Math.Between(10, cellRadius * 0.7);
      const randomAngle = Phaser.Math.FloatBetween(0, Math.PI * 2);
      const finalX = Math.cos(randomAngle) * randomRadius;
      const finalY = Math.sin(randomAngle) * randomRadius;
      
      this.finalizeOrganellePosition(organelle, finalX, finalY);
      return;
    }
    
    // Try each eligible zone in priority order
    for (const zone of eligibleZones) {
      // Check if zone is at capacity
      const currentOccupancy = this.organelleZoneOccupancy.get(zone.id) || 0;
      if (currentOccupancy >= zone.maxOccupancy) {
        console.log(`Zone ${zone.name} is at capacity (${currentOccupancy}/${zone.maxOccupancy}), trying next zone`);
        continue;
      }
      
      // Found a zone with space, try to position within this zone
      const position = this.findPositionInZone(zone, organelle);
      if (position) {
        // Successfully positioned in this zone
        this.organelleZoneOccupancy.set(zone.id, currentOccupancy + 1);
        this.finalizeOrganellePosition(organelle, position.x, position.y);
        console.log(`Positioned ${type} in zone ${zone.name} at (${position.x.toFixed(2)}, ${position.y.toFixed(2)})`);
        return;
      }
    }
    
    // If we get here, we couldn't find a suitable position in any zone
    // Use fallback positioning
    console.warn(`Could not find position for ${type} in any eligible zone, using fallback positioning`);
    const fallbackZone = eligibleZones[0]; // Use highest priority zone as fallback
    const minDist = fallbackZone.minDistanceFromCenter;
    const maxDist = fallbackZone.maxDistanceFromCenter;
    const distance = Phaser.Math.Between(minDist, maxDist);
    const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
    const finalX = Math.cos(angle) * distance;
    const finalY = Math.sin(angle) * distance;
    
    this.finalizeOrganellePosition(organelle, finalX, finalY);
  }
  
  /**
   * Finds a suitable position for an organelle within a specific zone
   * @param zone The zone to position within
   * @param organelle The organelle to position
   * @returns The position or null if no suitable position found
   */
  private findPositionInZone(zone: OrganelleZone, organelle: Organelle): {x: number, y: number} | null {
    const existingOrganelles = this.getOrganelles();
    const type = organelle.getType();
    const minDist = zone.minDistanceFromCenter;
    const maxDist = zone.maxDistanceFromCenter;
    const minDistanceBetween = zone.minDistanceBetweenOrganelles || 20;
    
    // Different positioning strategies based on distribution pattern
    switch (zone.distributionPattern || 'uniform') {
      case 'uniform':
        // Try to distribute evenly around the center
        return this.findUniformPositionInZone(zone, organelle, existingOrganelles, minDistanceBetween);
        
      case 'clustered':
        // Try to position near other organelles of the same type
        return this.findClusteredPositionInZone(zone, organelle, existingOrganelles, minDistanceBetween);
        
      case 'random':
      default:
        // Try random positions within the zone
        const maxAttempts = type === 'lysosome' || type === 'mitochondrion' ? 30 : 15; // More attempts for organelles that need careful positioning
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          // Generate random distance and angle within zone
          const distance = Phaser.Math.Between(minDist, maxDist);
          
          // Check if there are preferred or avoid angles
          let angle: number;
          if (zone.preferredAngles && zone.preferredAngles.length > 0) {
            // Pick a random preferred angle range
            const preferredRange = Phaser.Utils.Array.GetRandom(zone.preferredAngles);
            angle = Phaser.Math.FloatBetween(preferredRange.min, preferredRange.max);
          } else if (zone.avoidAngles && zone.avoidAngles.length > 0) {
            // Generate angle avoiding specified ranges
            angle = this.generateAngleAvoidingRanges(zone.avoidAngles);
          } else {
            // Completely random angle
            angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
          }
          
          const x = Math.cos(angle) * distance;
          const y = Math.sin(angle) * distance;
          
          // Check for overlap with existing organelles
          if (this.checkPositionValid(x, y, organelle, existingOrganelles, minDistanceBetween)) {
            // Log successful position for lysosomes to help with debugging
            if (type === 'lysosome') {
              console.log(`Found valid position for lysosome at (${x.toFixed(1)}, ${y.toFixed(1)}) on attempt ${attempt+1}/${maxAttempts}`);
            }
            return { x, y };
          } else if (type === 'lysosome' && attempt % 5 === 0) {
            // Log failures for lysosomes every few attempts
            console.log(`Failed to position lysosome at (${x.toFixed(1)}, ${y.toFixed(1)}) - attempt ${attempt+1}/${maxAttempts}`);
          }
        }
        
        if (type === 'lysosome') {
          console.warn(`Failed to find valid position for lysosome after ${maxAttempts} attempts`);
        }
        return null;
    }
  }
  
  /**
   * Find a position for an organelle with uniform distribution in a zone
   */
  private findUniformPositionInZone(
    zone: OrganelleZone, 
    organelle: Organelle, 
    existingOrganelles: Organelle[], 
    minDistanceBetween: number
  ): {x: number, y: number} | null {
    const type = organelle.getType();
    const minDist = zone.minDistanceFromCenter;
    const maxDist = zone.maxDistanceFromCenter;
    
    // Special handling for mitochondria to create more biologically accurate distribution
    if (type === 'mitochondrion') {
      // Find all existing mitochondria
      const existingMitochondria = existingOrganelles.filter(org => 
        org.getType() === 'mitochondrion' && 
        org !== organelle
      );
      
      const mitoCount = existingMitochondria.length;
      console.log(`Positioning mitochondrion #${mitoCount + 1}`);
      
      // Different positioning based on how many mitochondria we already have
      if (mitoCount === 0) {
        // First mitochondrion - place in preferred region if specified
        if (zone.preferredAngles && zone.preferredAngles.length > 0) {
          // Pick a random preferred angle range
          const preferredRange = Phaser.Utils.Array.GetRandom(zone.preferredAngles);
          const angle = Phaser.Math.FloatBetween(preferredRange.min, preferredRange.max);
          
          // Use middle distance in zone
          const distance = (minDist + maxDist) / 2;
          const x = Math.cos(angle) * distance;
          const y = Math.sin(angle) * distance;
          
          return { x, y };
        }
      } 
      else if (mitoCount === 1) {
        // Second mitochondrion - place approximately opposite to first one
        const firstMito = existingMitochondria[0];
        const firstAngle = Math.atan2(firstMito.y, firstMito.x);
        const firstDistance = Math.sqrt(firstMito.x * firstMito.x + firstMito.y * firstMito.y);
        
        // Add π radians (180°) plus a small variation for natural appearance
        const variation = Phaser.Math.FloatBetween(-Math.PI/8, Math.PI/8); // ±22.5°
        const newAngle = this.normalizeAngle(firstAngle + Math.PI + variation);
        
        // Use similar distance from center as first mitochondrion
        const newDistance = firstDistance * Phaser.Math.FloatBetween(0.9, 1.1);
        const x = Math.cos(newAngle) * newDistance;
        const y = Math.sin(newAngle) * newDistance;
        
        // Ensure position is within zone bounds
        if (newDistance >= minDist && newDistance <= maxDist) {
          return { x, y };
        }
      }
      else if (mitoCount === 2) {
        // Third mitochondrion - try to form a triangle pattern
        const angles = existingMitochondria.map(mito => Math.atan2(mito.y, mito.x));
        
        // Calculate angle roughly 120° from both existing mitochondria
        // This creates a triangular pattern
        const avgAngle = (angles[0] + angles[1]) / 2;
        const thirdAngle = this.normalizeAngle(avgAngle + Math.PI);
        
        // Use average distance of existing mitochondria
        const avgDistance = existingMitochondria.reduce((sum, mito) => {
          return sum + Math.sqrt(mito.x * mito.x + mito.y * mito.y);
        }, 0) / existingMitochondria.length;
        
        // Add a small random variation
        const finalDistance = avgDistance * Phaser.Math.FloatBetween(0.9, 1.1);
        const x = Math.cos(thirdAngle) * finalDistance;
        const y = Math.sin(thirdAngle) * finalDistance;
        
        if (finalDistance >= minDist && finalDistance <= maxDist &&
            this.checkPositionValid(x, y, organelle, existingOrganelles, minDistanceBetween)) {
          return { x, y };
        }
      }
      else {
        // For 4+ mitochondria, try to fill in gaps in the existing distribution
        
        // Sort existing mitochondria by angle
        const sortedByAngle = [...existingMitochondria].sort((a, b) => {
          return Math.atan2(a.y, a.x) - Math.atan2(b.y, b.x);
        });
        
        // Calculate average distance from center
        const avgDistance = sortedByAngle.reduce((sum, mito) => {
          return sum + Math.sqrt(mito.x * mito.x + mito.y * mito.y);
        }, 0) / sortedByAngle.length;
        
        // Find the largest angular gap
        let maxGap = 0;
        let gapMidAngle = 0;
        
        for (let i = 0; i < sortedByAngle.length; i++) {
          const currentAngle = Math.atan2(sortedByAngle[i].y, sortedByAngle[i].x);
          const nextAngle = Math.atan2(
            sortedByAngle[(i + 1) % sortedByAngle.length].y,
            sortedByAngle[(i + 1) % sortedByAngle.length].x
          );
          
          // Calculate gap (handle wraparound)
          let gap = nextAngle - currentAngle;
          if (gap < 0) gap += Math.PI * 2;
          
          if (gap > maxGap) {
            maxGap = gap;
            gapMidAngle = this.normalizeAngle(currentAngle + gap / 2);
          }
        }
        
        // Position in the middle of the largest gap
        const gapDistance = avgDistance * Phaser.Math.FloatBetween(0.95, 1.05);
        const x = Math.cos(gapMidAngle) * gapDistance;
        const y = Math.sin(gapMidAngle) * gapDistance;
        
        if (gapDistance >= minDist && gapDistance <= maxDist &&
            this.checkPositionValid(x, y, organelle, existingOrganelles, minDistanceBetween)) {
          return { x, y };
        }
      }
    }
    
    // For non-mitochondria or if mitochondria positioning failed, use standard uniform distribution
    // For uniform distribution, divide the circle into segments
    const currentCount = existingOrganelles.filter(org => 
      org.getType() === type && 
      org !== organelle
    ).length;
    
    // Determine optimal angle spacing
    const angleStep = Math.PI * 2 / (currentCount + 1);
    
    // Try different distances within the zone
    const distanceStep = (maxDist - minDist) / 3;
    const distances = [
      minDist + distanceStep, 
      minDist + distanceStep * 2, 
      (minDist + maxDist) / 2
    ];
    
    // Try each distance and angle combination
    for (const distance of distances) {
      for (let i = 0; i < currentCount + 1; i++) {
        const angle = i * angleStep;
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance;
        
        if (this.checkPositionValid(x, y, organelle, existingOrganelles, minDistanceBetween)) {
          return { x, y };
        }
      }
    }
    
    // If uniform distribution fails, fall back to random positioning within zone
    return this.findRandomPositionInZone(zone, organelle, existingOrganelles, minDistanceBetween);
  }
  
  /**
   * Find a position for an organelle clustered near similar organelles
   */
  private findClusteredPositionInZone(
    zone: OrganelleZone, 
    organelle: Organelle, 
    existingOrganelles: Organelle[],
    minDistanceBetween: number
  ): {x: number, y: number} | null {
    const type = organelle.getType();
    
    // Find existing organelles of the same type in this zone
    const sameTypeOrganelles = existingOrganelles.filter(org => 
      org.getType() === type && 
      org !== organelle
    );
    
    if (sameTypeOrganelles.length === 0) {
      // No existing organelles of this type, use random positioning
      return this.findRandomPositionInZone(zone, organelle, existingOrganelles, minDistanceBetween);
    }
    
    // Special handling for lysosomes - try to create biologically accurate clusters
    if (type === 'lysosome') {
      console.log(`Positioning lysosome in clustered formation with ${sameTypeOrganelles.length} existing lysosomes`);
      
      // Try to position near an existing lysosome, but maintain proper spacing
      for (const existingOrg of sameTypeOrganelles) {
        const baseX = existingOrg.x;
        const baseY = existingOrg.y;
        
        // Try different distances and angles around the existing lysosome
        // Using more angles for more thorough search
        const minSpacing = 20; // Minimum distance between lysosomes
        const spacingVariations = [1.0, 1.2, 1.4, 1.6, 1.8]; // Try different distances
        
        for (const spacingFactor of spacingVariations) {
          const tryDistance = minSpacing * spacingFactor;
          
          // Try 16 different angles (every 22.5 degrees)
          for (let i = 0; i < 16; i++) {
            const angleOffset = (Math.PI * 2 / 16) * i;
            const x = baseX + Math.cos(angleOffset) * tryDistance;
            const y = baseY + Math.sin(angleOffset) * tryDistance;
            
            // Verify position is within zone bounds
            const distanceFromCenter = Math.sqrt(x * x + y * y);
            if (distanceFromCenter < zone.minDistanceFromCenter || distanceFromCenter > zone.maxDistanceFromCenter) {
              continue;
            }
            
            if (this.checkPositionValid(x, y, organelle, existingOrganelles, minDistanceBetween)) {
              console.log(`Positioned lysosome at (${x.toFixed(1)}, ${y.toFixed(1)}) near existing lysosome at (${baseX.toFixed(1)}, ${baseY.toFixed(1)})`);
              return { x, y };
            }
          }
        }
      }
      
      console.log(`Couldn't position lysosome near existing ones, trying random position`);
      return this.findRandomPositionInZone(zone, organelle, existingOrganelles, minDistanceBetween);
    }
    
    // Try to position near an existing organelle of the same type
    for (const existingOrg of sameTypeOrganelles) {
      // Try positions around the existing organelle
      const baseX = existingOrg.x;
      const baseY = existingOrg.y;
      
      // Try different angles around the existing organelle
      for (let angleOffset = 0; angleOffset < Math.PI * 2; angleOffset += Math.PI / 4) {
        const x = baseX + Math.cos(angleOffset) * minDistanceBetween * 1.2;
        const y = baseY + Math.sin(angleOffset) * minDistanceBetween * 1.2;
        
        // Verify position is within zone bounds
        const distanceFromCenter = Math.sqrt(x * x + y * y);
        if (distanceFromCenter < zone.minDistanceFromCenter || distanceFromCenter > zone.maxDistanceFromCenter) {
          continue;
        }
        
        if (this.checkPositionValid(x, y, organelle, existingOrganelles, minDistanceBetween)) {
          return { x, y };
        }
      }
    }
    
    // If clustering fails, fall back to random positioning
    return this.findRandomPositionInZone(zone, organelle, existingOrganelles, minDistanceBetween);
  }
  
  /**
   * Find a random position in a zone avoiding overlaps
   */
  private findRandomPositionInZone(
    zone: OrganelleZone, 
    organelle: Organelle, 
    existingOrganelles: Organelle[],
    minDistanceBetween: number
  ): {x: number, y: number} | null {
    const minDist = zone.minDistanceFromCenter;
    const maxDist = zone.maxDistanceFromCenter;
    const maxAttempts = 20;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Generate random position within the zone
      const distance = Phaser.Math.Between(minDist, maxDist);
      const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;
      
      if (this.checkPositionValid(x, y, organelle, existingOrganelles, minDistanceBetween)) {
        return { x, y };
      }
    }
    
    return null;
  }
  
  /**
   * Generate an angle that avoids the specified ranges
   */
  private generateAngleAvoidingRanges(avoidRanges: {min: number, max: number}[]): number {
    // Create a list of all valid angle ranges
    const validRanges: {min: number, max: number}[] = [];
    let lastMax = 0;
    
    // Sort avoid ranges by min angle
    const sortedRanges = [...avoidRanges].sort((a, b) => a.min - b.min);
    
    // Find gaps between avoid ranges
    for (const range of sortedRanges) {
      if (range.min > lastMax) {
        validRanges.push({min: lastMax, max: range.min});
      }
      lastMax = Math.max(lastMax, range.max);
    }
    
    // Add final range if needed
    if (lastMax < Math.PI * 2) {
      validRanges.push({min: lastMax, max: Math.PI * 2});
    }
    
    if (validRanges.length === 0) {
      // No valid ranges, return random angle
      return Phaser.Math.FloatBetween(0, Math.PI * 2);
    }
    
    // Select a random valid range and generate angle within it
    const selectedRange = Phaser.Utils.Array.GetRandom(validRanges);
    return Phaser.Math.FloatBetween(selectedRange.min, selectedRange.max);
  }
  
  /**
   * Check if a position is valid for an organelle (no overlaps)
   */
  private checkPositionValid(
    x: number, 
    y: number, 
    organelle: Organelle, 
    existingOrganelles: Organelle[],
    minDistance: number
  ): boolean {
    for (const existingOrg of existingOrganelles) {
      // Skip the current organelle
      if (existingOrg === organelle) continue;
      
      // Calculate distance to existing organelle
      const dx = existingOrg.x - x;
      const dy = existingOrg.y - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Adjust minimum distance based on organelle types
      if (existingOrg.getType() === 'nucleus') {
        // Make sure to stay away from nucleus
        if (distance < 45) { // Nucleus radius + buffer
          return false;
        }
      } 
      // Use stricter check for mitochondria to prevent overlapping
      else if (existingOrg.getType() === 'mitochondrion' && organelle.getType() === 'mitochondrion') {
        // Much larger minimum distance between mitochondria to ensure no overlap
        // Mitochondria are 20px in size plus 4% pulsation, so we need at least 35px between centers
        if (distance < 35) { // Increased from 30 to account for pulsation
          return false;
        }
      }
      // Special handling for lysosome positioning
      else if (existingOrg.getType() === 'lysosome' && organelle.getType() === 'lysosome') {
        // Lysosomes are 15px in size with 10% pulsation, so need at least 18px between centers
        // Use a slightly larger buffer to ensure they never overlap visually
        if (distance < 20) { // ~15px (size) + ~2px (pulsation) + 3px (buffer)
          console.log(`Rejecting lysosome position: too close to another lysosome (${distance.toFixed(1)}px)`);
          return false;
        }
      }
      // Special case for lysosome near mitochondrion to prevent overlap
      else if ((existingOrg.getType() === 'lysosome' && organelle.getType() === 'mitochondrion') ||
               (existingOrg.getType() === 'mitochondrion' && organelle.getType() === 'lysosome')) {
        // Calculate minimum safe distance between lysosome and mitochondrion
        // Lysosome: ~15px + pulsation, Mitochondrion: ~20px + pulsation
        // Need at least the sum of their radii plus a small buffer
        if (distance < 25) { // (15/2 + 20/2) + pulsation + buffer
          console.log(`Rejecting position: lysosome and mitochondrion too close (${distance.toFixed(1)}px)`);
          return false;
        }
      }
      else if (existingOrg.getType() === organelle.getType()) {
        // Keep same-type organelles further apart
        if (distance < minDistance * 1.2) {
          return false;
        }
      } 
      else if (distance < minDistance) {
        // General minimum distance for different types
        return false;
      }
    }
    
    return true;
  }
  
  /**
   * Finalizes the positioning of an organelle by setting its position and properties
   */
  private finalizeOrganellePosition(organelle: Organelle, x: number, y: number): void {
    // Final position validation for mitochondria
    let finalX = x;
    let finalY = y;
    
    // For mitochondria, double-check positioning is within allowed zone
    if (organelle.getType() === 'mitochondrion') {
      const mitochondrionZone = this.organelleZones.find(zone => 
        zone.organelleTypes.includes('mitochondrion')
      );
      
      if (mitochondrionZone) {
        // Check if position is within allowed zone
        const distanceFromCenter = Math.sqrt(x * x + y * y);
        const isOutsideZone = distanceFromCenter < mitochondrionZone.minDistanceFromCenter || 
                              distanceFromCenter > mitochondrionZone.maxDistanceFromCenter;
        
        if (isOutsideZone) {
          // Recalculate position to be in the middle of the zone
          const angle = Math.atan2(y, x);
          const safeDistance = (mitochondrionZone.minDistanceFromCenter + mitochondrionZone.maxDistanceFromCenter) / 2;
          finalX = Math.cos(angle) * safeDistance;
          finalY = Math.sin(angle) * safeDistance;
          
          console.warn(`Corrected initial mitochondrion position from (${x.toFixed(2)}, ${y.toFixed(2)}) to (${finalX.toFixed(2)}, ${finalY.toFixed(2)})`);
        }
      }
    }
    
    // Set both the position and the target position to keep the organelle in place
    organelle.setPosition(finalX, finalY);
    
    // Access and update the targetPosition directly
    const organelleAny = organelle as any;
    if (organelleAny.targetPosition) {
      organelleAny.targetPosition.set(finalX, finalY);
      
      // Also update initialPosition for mitochondria
      if (organelle.getType() === 'mitochondrion' && organelleAny.initialPosition) {
        organelleAny.initialPosition.set(finalX, finalY);
        console.log(`Set mitochondrion initial and target position to (${finalX}, ${finalY})`);
      }
    }
    
    // Configure behavior based on organelle type
    if (organelleAny.config) {
      const type = organelle.getType();
      
      // Most organelles should maintain position
      organelleAny.config.maintainPosition = true;
      
      if (type === 'mitochondrion') {
        // Mitochondria should be completely fixed
        organelleAny.config.wobbleAmount = 0;
        organelleAny.wobbleOffset = new Phaser.Math.Vector2(0, 0);
        organelleAny.velocity = new Phaser.Math.Vector2(0, 0);
      } else if (type === 'vesicle' || type === 'endosome') {
        // Small vesicles and endosomes can move more freely
        organelleAny.config.maintainPosition = false;
        organelleAny.config.wobbleAmount = 0.3;
      }
    }
    
    console.log(`Positioned ${organelle.getType()} at (${finalX}, ${finalY})`);
  }

  // Add a method to create positioning guides for debug purposes
  private createPositioningGuides(): void {
    const graphics = this.scene.add.graphics();
    this.add(graphics);
    
    // Color scheme for zones
    const zoneColors = [
      0xFF0000, // Red - nuclear zone
      0x00FF00, // Green - perinuclear
      0x0000FF, // Blue - intermediate
      0xFF00FF  // Magenta - peripheral
    ];
    
    // Draw zones
    this.organelleZones.forEach((zone, index) => {
      const color = zoneColors[index % zoneColors.length];
      const alpha = 0.15;
      
      // Fill zone with transparent color
      graphics.fillStyle(color, alpha);
      graphics.fillCircle(0, 0, zone.maxDistanceFromCenter);
      
      // Draw inner boundary
      graphics.lineStyle(1, color, 0.5);
      graphics.strokeCircle(0, 0, zone.minDistanceFromCenter);
      
      // Draw outer boundary
      graphics.lineStyle(1, color, 0.8);
      graphics.strokeCircle(0, 0, zone.maxDistanceFromCenter);
      
      // Add zone label
      const midRadius = (zone.minDistanceFromCenter + zone.maxDistanceFromCenter) / 2;
      const labelAngle = Math.PI / 4; // 45 degrees
      const labelX = Math.cos(labelAngle) * midRadius;
      const labelY = Math.sin(labelAngle) * midRadius;
      
      const textStyle = { fontSize: '9px', color: '#FFFFFF', backgroundColor: `rgba(${color >> 16},${(color >> 8) & 0xFF},${color & 0xFF},0.5)` };
      const zoneLabel = this.scene.add.text(labelX, labelY, zone.name, textStyle);
      zoneLabel.setOrigin(0.5);
      this.add(zoneLabel);
      
      // Add organelle type info
      const typeLabel = this.scene.add.text(
        labelX, 
        labelY + 10, 
        `(${zone.organelleTypes.join(', ')})`, 
        { fontSize: '7px', color: '#FFFFFF', backgroundColor: '#00000080' }
      );
      typeLabel.setOrigin(0.5);
      this.add(typeLabel);
    });
    
    // Draw specific highlights for important organelles
    // Nucleus center
    graphics.lineStyle(2, 0xFFFF00, 0.8);
    graphics.strokeCircle(0, 0, 5);
    
    // Add legend in bottom corner
    const legendBg = this.scene.add.graphics();
    legendBg.fillStyle(0x000000, 0.7);
    legendBg.fillRect(-95, 70, 190, 25);
    this.add(legendBg);
    
    const legendText = this.scene.add.text(
      -90, 75, 
      "Organelle Positioning Zones (Debug View)", 
      { fontSize: '8px', color: '#FFFFFF' }
    );
    this.add(legendText);
    
    console.log('Added organelle positioning guides for debugging');
  }

  /**
   * Adjusts organelle positions during cell deformation
   * This simulates how organelles shift within the cytoplasm when the cell changes shape
   * @param deformAmount The amount of deformation (0-1)
   * @param deformAngle The angle of deformation in radians
   */
  adjustOrganellePositionsForDeformation(deformAmount: number, deformAngle: number): void {
    // Skip minor deformations
    if (deformAmount < 0.1) return;
    
    // Cap deformation amount
    deformAmount = Math.min(deformAmount, 1.0);
    
    // Adjust each organelle position based on its type and position
    for (const organelle of this.organelles) {
      const type = organelle.getType();
      
      // Skip nucleus - it should remain fixed at center
      if (type === 'nucleus') continue;
      
      // Set movement factor based on organelle type
      // Mitochondria move very minimally
      let movementFactor = 0.4; // Default for most organelles
      
      if (type === 'mitochondrion') {
        // Mitochondria should only move slightly - simulating fluid dynamics
        movementFactor = 0.08; // Very minimal movement factor
      } else if (type === 'lysosome') {
        // Lysosomes should be almost as stable as mitochondria
        movementFactor = 0.10; // Very minimal movement factor
      } else if (type === 'vesicle' || type === 'endosome') {
        // Small vesicles move more freely in cytoplasm
        movementFactor = 0.5;
      }
      
      // Calculate current angle and distance from center
      const angle = Math.atan2(organelle.y, organelle.x);
      const distance = Math.sqrt(organelle.x * organelle.x + organelle.y * organelle.y);
      
      // Calculate angle difference from deformation direction
      const angleDiff = this.normalizeAngle(angle - deformAngle);
      
      // Calculate movement based on alignment with deformation direction
      let moveAmount = 0;
      
      if (Math.abs(angleDiff) < Math.PI * 0.3) {
        // Aligned with deformation, move outward
        moveAmount = deformAmount * movementFactor * distance * 0.2;
      } else if (Math.abs(angleDiff) > Math.PI * 0.7) {
        // Opposite to deformation, move slightly inward
        moveAmount = -deformAmount * movementFactor * distance * 0.1;
      } else {
        // Perpendicular to deformation, move very slightly inward
        moveAmount = -deformAmount * movementFactor * distance * 0.05;
      }
      
      // For mitochondria and lysosomes, update initial position to avoid recentering
      // This will maintain the slight movement caused by deformation
      if (type === 'mitochondrion' || type === 'lysosome') {
        // Calculate new position
        const newDistance = Math.max(distance + moveAmount, 10);
        const newX = Math.cos(angle) * newDistance;
        const newY = Math.sin(angle) * newDistance;
        
        // Access internal properties
        const organelleAny = organelle as any;
        
        // Update position with damping (smoother movement)
        organelle.setPosition(
          organelle.x + (newX - organelle.x) * 0.3,
          organelle.y + (newY - organelle.y) * 0.3
        );
        
        // Update initial position slightly to allow
        // this deformation-based movement to persist
        if (organelleAny.initialPosition) {
          organelleAny.initialPosition.x += (newX - organelle.x) * 0.05;
          organelleAny.initialPosition.y += (newY - organelle.y) * 0.05;
        }
      } else {
        // For other organelles, apply normal movement
        // Calculate new position
        const newDistance = Math.max(distance + moveAmount, 10);
        const newX = Math.cos(angle) * newDistance;
        const newY = Math.sin(angle) * newDistance;
        
        // Update organelle position
        organelle.setPosition(newX, newY);
        
        // Update targetPosition for physics-controlled organelles
        const organelleAny = organelle as any;
        if (organelleAny.targetPosition) {
          organelleAny.targetPosition.set(newX, newY);
        }
      }
    }
  }

  /**
   * Checks for and corrects overlapping organelles (mitochondria and lysosomes)
   */
  private checkForOverlappingOrganelles(): void {
    // Get all mitochondria and lysosomes
    const mitochondria = this.organelles.filter(org => org.getType() === 'mitochondrion');
    const lysosomes = this.organelles.filter(org => org.getType() === 'lysosome');
    
    // Skip if we have no organelles to check
    if (mitochondria.length < 2 && lysosomes.length === 0) return;
    
    // Check mitochondria pairs for overlap
    this.checkOrganellePairsForOverlap(mitochondria, mitochondria, 35); // 35px minimum distance
    
    // Check lysosome pairs for overlap
    this.checkOrganellePairsForOverlap(lysosomes, lysosomes, 20); // 20px minimum distance
    
    // Check mitochondria-lysosome overlap
    this.checkOrganellePairsForOverlap(mitochondria, lysosomes, 25); // 25px minimum distance
  }
  
  /**
   * Checks for and corrects overlapping organelles between two groups
   * @param groupA First group of organelles
   * @param groupB Second group of organelles
   * @param minDistance Minimum allowed distance between organelles
   */
  private checkOrganellePairsForOverlap(
    groupA: Organelle[], 
    groupB: Organelle[],
    minDistance: number
  ): void {
    // If either group is empty, skip
    if (groupA.length === 0 || groupB.length === 0) return;
    
    // Type names for logging
    const typeA = groupA[0].getType();
    const typeB = groupB[0].getType();
    const isSameGroup = groupA === groupB;
    
    // Check all pairs for overlap
    for (let i = 0; i < groupA.length; i++) {
      const orgA = groupA[i];
      
      // If checking the same group, start from i+1 to avoid checking the same pair twice
      // Otherwise, check all elements in group B
      const startJ = isSameGroup ? i + 1 : 0;
      
      for (let j = startJ; j < groupB.length; j++) {
        const orgB = groupB[j];
        
        // Skip if it's the same organelle (can happen when A and B are the same group)
        if (orgA === orgB) continue;
        
        // Calculate distance between the two organelles
        const dx = orgB.x - orgA.x;
        const dy = orgB.y - orgA.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Check if they're too close
        if (distance < minDistance) {
          // Log when adjusting positions (but only occasionally)
          if (Math.random() < 0.1) {
            console.log(`Adjusting overlapping ${typeA}-${typeB}: distance=${distance.toFixed(1)}px, min=${minDistance}px`);
          }
          
          // Move both organelles away from each other
          // Calculate the angle between them
          const angle = Math.atan2(dy, dx);
          
          // Calculate the amount each should move (half the overlap plus a small extra buffer)
          const moveAmount = ((minDistance - distance) / 2) + 0.5; // Added 0.5px buffer
          
          // Calculate the new positions
          const moveX1 = -Math.cos(angle) * moveAmount;
          const moveY1 = -Math.sin(angle) * moveAmount;
          const moveX2 = Math.cos(angle) * moveAmount;
          const moveY2 = Math.sin(angle) * moveAmount;
          
          // Get the internal properties for smooth adjustment
          const orgAny = orgA as any;
          const orgBny = orgB as any;
          
          // Update positions gently with the Phaser tween system for smoother transitions
          if (this.scene && distance < minDistance - 2) { // Only use tweens for significant overlaps
            // Create tween for first organelle
            this.scene.tweens.add({
              targets: orgA,
              x: orgA.x + moveX1,
              y: orgA.y + moveY1,
              duration: 300,
              ease: 'Sine.easeOut'
            });
            
            // Create tween for second organelle
            this.scene.tweens.add({
              targets: orgB,
              x: orgB.x + moveX2,
              y: orgB.y + moveY2,
              duration: 300,
              ease: 'Sine.easeOut'
            });
          } else {
            // For minor adjustments, use direct position update
            orgA.x += moveX1;
            orgA.y += moveY1;
            orgB.x += moveX2;
            orgB.y += moveY2;
          }
          
          // Also update internal positioning properties
          if (orgAny.initialPosition) {
            orgAny.initialPosition.x += moveX1;
            orgAny.initialPosition.y += moveY1;
          }
          
          if (orgBny.initialPosition) {
            orgBny.initialPosition.x += moveX2;
            orgBny.initialPosition.y += moveY2;
          }
          
          // Also update target positions
          if (orgAny.targetPosition) {
            orgAny.targetPosition.x = orgAny.initialPosition?.x || orgA.x;
            orgAny.targetPosition.y = orgAny.initialPosition?.y || orgA.y;
          }
          
          if (orgBny.targetPosition) {
            orgBny.targetPosition.x = orgBny.initialPosition?.x || orgB.x;
            orgBny.targetPosition.y = orgBny.initialPosition?.y || orgB.y;
          }
        }
      }
    }
  }

  /**
   * Updates the occupancy counts for each organelle zone
   */
  private updateZoneOccupancy(): void {
    // Reset all zone occupancy counts
    this.organelleZones.forEach(zone => {
      this.organelleZoneOccupancy.set(zone.id, 0);
    });
    
    // Count organelles in each zone
    this.organelles.forEach(org => {
      const orgType = org.getType();
      const distance = Math.sqrt(org.x * org.x + org.y * org.y);
      
      // Find which zone this organelle belongs to
      const zone = this.organelleZones.find(z => 
        z.organelleTypes.includes(orgType) && 
        distance >= z.minDistanceFromCenter && 
        distance <= z.maxDistanceFromCenter
      );
      
      if (zone) {
        // Increment the zone's occupancy count
        const currentCount = this.organelleZoneOccupancy.get(zone.id) || 0;
        this.organelleZoneOccupancy.set(zone.id, currentCount + 1);
      }
    });
    
    // Log zone occupancy for debugging (only occasionally)
    if (Math.random() < 0.1) {
      console.log('Zone occupancy updated:', Object.fromEntries(this.organelleZoneOccupancy));
    }
  }

  /**
   * Ensures that all organelles stay within the cell membrane boundaries
   * Critical for maintaining realistic cell simulation
   * @param organelle The organelle to check and constrain
   */
  private enforceOrganelleCellBoundary(organelle: Organelle): void {
    // Skip nucleus as it should always be at center
    if (organelle.getType() === 'nucleus') {
      return;
    }
    
    // Calculate distance from center
    const distanceFromCenter = Math.sqrt(organelle.x * organelle.x + organelle.y * organelle.y);
    
    // Cell membrane radius minus a buffer (cell radius is 100)
    // This creates a small margin between organelles and the membrane
    const maxSafeDistance = 95; // Back to original value
    
    // For lysosomes, also check that they're not too close to the nucleus
    if (organelle.getType() === 'lysosome') {
      const nucleusRadius = 40; // Adjusted down from 45 to reflect smaller nucleus (8px radius + buffer)
      const minSafeDistance = 47; // Adjusted to prevent jitter but give more space near nucleus
      
      if (distanceFromCenter < minSafeDistance) {
        console.log(`Lysosome too close to nucleus: ${distanceFromCenter.toFixed(1)}px from center`);
        
        // Calculate angle from center
        const angle = Math.atan2(organelle.y, organelle.x);
        
        // Calculate safe position outside nucleus
        const safeX = Math.cos(angle) * minSafeDistance;
        const safeY = Math.sin(angle) * minSafeDistance;
        
        // Update position
        organelle.setPosition(safeX, safeY);
        
        // Update target position and initial position for lysosome
        const organelleAny = organelle as any;
        if (organelleAny.targetPosition) {
          organelleAny.targetPosition.set(safeX, safeY);
        }
        
        if (organelleAny.initialPosition) {
          organelleAny.initialPosition.set(safeX, safeY);
        }
        
        return; // Skip the outer boundary check after fixing inner boundary
      }
    }
    
    // If organelle is too close to the edge or outside the cell
    if (distanceFromCenter > maxSafeDistance) {
      console.log(`Organelle (${organelle.getType()}) outside cell boundary: ${distanceFromCenter.toFixed(1)}px from center`);
      
      // Calculate angle from center
      const angle = Math.atan2(organelle.y, organelle.x);
      
      // Calculate safe position
      const safeX = Math.cos(angle) * maxSafeDistance;
      const safeY = Math.sin(angle) * maxSafeDistance;
      
      // Update position
      organelle.setPosition(safeX, safeY);
      
      // Update target position for organelles that use it
      const organelleAny = organelle as any;
      if (organelleAny.targetPosition) {
        organelleAny.targetPosition.set(safeX, safeY);
      }
      
      // Update initial position for floating motion
      if (organelleAny.initialPosition) {
        organelleAny.initialPosition.set(safeX, safeY);
      }
    }
  }

  /**
   * Set the membrane expansion stage
   * @param stage New membrane stage (1-4)
   * @param sizeMultiplier Size multiplier for this stage
   * @param speedMultiplier Speed multiplier for this stage
   */
  public setMembraneStage(stage: number, sizeMultiplier: number, speedMultiplier: number): void {
    if (this.membraneStage === stage) return;
    
    console.log(`Expanding membrane to stage ${stage} (size: ${sizeMultiplier}, speed: ${speedMultiplier})`);
    
    // Store the new stage
    this.membraneStage = stage;
    
    // Resize the membrane
    this.resizeMembrane(sizeMultiplier);
    
    // Resize organelle zones
    this.resizeOrganelleZones(sizeMultiplier);
    
    // Reposition organelles
    this.repositionOrganellesInZones();
    
    // Update resource capacities based on new membrane stage
    this.updateMaxATP();
    
    // Update max speed based on the speed multiplier
    this.maxSpeed = 10 * speedMultiplier;
    
    // Emit an event that the membrane has expanded
    this.emit('membraneExpanded', { stage, sizeMultiplier, speedMultiplier });
  }
  
  /**
   * Get the current membrane stage
   */
  public getMembraneStage(): number {
    return this.membraneStage;
  }
  
  /**
   * Resize the membrane and organelle zones based on the size multiplier
   */
  private resizeMembrane(sizeMultiplier: number): void {
    // Base size is 100
    const newRadius = 100 * sizeMultiplier;
    
    // Update membrane size
    this.membrane.resize(newRadius);
    
    // Update interaction area
    this.setInteractive(new Phaser.Geom.Circle(0, 0, newRadius), Phaser.Geom.Circle.Contains);
    
    // Resize organelle zones
    this.resizeOrganelleZones(sizeMultiplier);
  }
  
  /**
   * Resize all organelle zones based on the size multiplier
   */
  private resizeOrganelleZones(sizeMultiplier: number): void {
    // Store original values for zones if they don't exist yet
    this.organelleZones.forEach(zone => {
      if (!(zone as any)._originalMinDistance) {
        (zone as any)._originalMinDistance = zone.minDistanceFromCenter;
        (zone as any)._originalMaxDistance = zone.maxDistanceFromCenter;
      }
      
      // Scale the distances
      zone.minDistanceFromCenter = (zone as any)._originalMinDistance * sizeMultiplier;
      zone.maxDistanceFromCenter = (zone as any)._originalMaxDistance * sizeMultiplier;
      
      // If this zone has minimum distances between organelles, scale those too
      if (zone.minDistanceBetweenOrganelles) {
        if (!(zone as any)._originalMinDistanceBetween) {
          (zone as any)._originalMinDistanceBetween = zone.minDistanceBetweenOrganelles;
        }
        zone.minDistanceBetweenOrganelles = (zone as any)._originalMinDistanceBetween * sizeMultiplier;
      }
    });
    
    // Reposition existing organelles according to the new zones
    this.repositionOrganellesInZones();
  }
  
  /**
   * Reposition organelles according to their zones
   */
  private repositionOrganellesInZones(): void {
    // Reposition each organelle within its designated zone
    this.organelles.forEach(organelle => {
      const organelleType = organelle.getType();
      
      // Find the zone this organelle belongs to
      const zone = this.findZoneForOrganelleType(organelleType);
      if (!zone) return;
      
      // Calculate current distance from center
      const currDist = Math.sqrt(organelle.x * organelle.x + organelle.y * organelle.y);
      
      // Calculate target min and max distance for repositioning
      const targetMin = zone.minDistanceFromCenter;
      const targetMax = zone.maxDistanceFromCenter;
      
      // If the organelle is outside the zone boundaries, move it
      if (currDist < targetMin || currDist > targetMax) {
        // Calculate the angle to maintain relative positioning
        const angle = Math.atan2(organelle.y, organelle.x);
        
        // Calculate new position within zone boundaries
        let newDist = currDist;
        
        if (currDist < targetMin) {
          newDist = targetMin;
        } else if (currDist > targetMax) {
          newDist = targetMax;
        }
        
        // Calculate new x, y coordinates
        const newX = Math.cos(angle) * newDist;
        const newY = Math.sin(angle) * newDist;
        
        // Set the new position with animation
        this.scene.tweens.add({
          targets: organelle,
          x: newX,
          y: newY,
          duration: 500,
          ease: 'Power2'
        });
      }
    });
  }
  
  /**
   * Find the zone for a specific organelle type
   */
  private findZoneForOrganelleType(organelleType: string): OrganelleZone | null {
    return this.organelleZones.find(zone => zone.organelleTypes.includes(organelleType)) || null;
  }

  /**
   * Updates resource production from all organelles
   * @param delta Time elapsed since last update
   */
  private updateResourceProduction(delta: number): void {
    // Accumulate time until we hit the next update interval
    this.resourceUpdateTimer += delta;

    // Skip if not enough time passed for an update
    if (this.resourceUpdateTimer < this.resourceUpdateInterval) {
      return;
    }

    // Calculate time elapsed in seconds since last update
    const elapsedSeconds = this.resourceUpdateTimer / 1000;
    this.resourceUpdateTimer = 0; // Reset timer
    
    // Check for mitochondria - we'll need this for both regeneration and production
    const mitochondria = this.getOrganellesByType('mitochondrion');
    
    // Natural ATP regeneration
    if (this.atp < this.maxATP) {
      const prevATP = this.atp;
      const naturalRegeneration = calculateATPRegeneration({
        isMoving: this.isMoving,
        elapsedSeconds,
        currentATP: this.atp,
        maxATP: this.maxATP
      });
      this.atp = Math.min(this.maxATP, this.atp + naturalRegeneration);
      
      // Track regeneration for network sync - ensure we don't use Math.floor or round
      const actualRegeneration = this.atp - prevATP;
      this.accumulatedATP += actualRegeneration;
      this.naturalATPRegeneration += actualRegeneration; // Track natural regeneration separately
      this.resourceUpdateNeeded = true;
      
    }
    
    // If no mitochondria, we can skip the production calculation but make sure
    // we still mark for synchronization since we did natural regeneration
    if (mitochondria.length === 0) {
      return;
    }
    
    // Get active mitochondria (those that can produce ATP)
    const activeMitochondria = mitochondria.filter(organelle => {
      if (typeof (organelle as any).isProducing === 'function') {
        return (organelle as any).isProducing();
      }
      return true; // Default to active if no isProducing method
    });
    
    // Calculate production and consumption rates regardless of active state
    // Mitochondria should always consume glucose when they exist
    let totalATPProductionRate = 0;
    let totalGlucoseConsumptionRate = 0;
    
    // For all mitochondria: compute potential glucose consumption
    mitochondria.forEach(organelle => {
      // Cast to any to access mitochondrion-specific methods
      const mito = organelle as any;
      const rates = typeof mito.getProductionRates === 'function' ? 
                   mito.getProductionRates() : 
                   { atp: 1, glucose: 0.1 };
                     
      // All mitochondria consume glucose at their base rate
      totalGlucoseConsumptionRate += rates.glucose;
      
      // But only active ones produce ATP
      if (typeof mito.isProducing === 'function' && mito.isProducing()) {
        totalATPProductionRate += rates.atp;
      }
    });
    
    const production = calculateMitochondriaProduction({
      mitochondriaCount: 1,
      elapsedSeconds,
      glucose: this.glucose,
      currentATP: this.atp,
      maxATP: this.maxATP,
      glucosePerMitoPerSecond: totalGlucoseConsumptionRate,
      atpPerMitoPerSecond: totalATPProductionRate
    });

    if (production.glucoseConsumed > 0) {
      this.glucose -= production.glucoseConsumed;
      this.accumulatedGlucose += production.glucoseConsumed;
      this.mitochondriaGlucoseConsumption += production.glucoseConsumed;
      this.resourceUpdateNeeded = true;
    }

    if (production.atpProduced > 0) {
      this.atp = Math.min(this.maxATP, this.atp + production.atpProduced);
      this.accumulatedATP += production.atpProduced;
      this.mitochondriaATPProduction += production.atpProduced;
      this.resourceUpdateNeeded = true;
    }
    
    // Update resource states in all mitochondria
    this.updateMitochondriaResources();
  }

  /**
   * Returns detailed resource production information with source breakdowns.
   * Use this for updating the server with source-specific information.
   */
  public getResourceProductionDetails(): {
    total: { atp: number; glucose: number };
    sources: {
      natural: { atp: number };
      mitochondria: { atp: number; glucose: number; count: number };
    };
    needsUpdate: boolean;
  } {
    // Calculate if we need to send an update (either flagged or significant change)
    const smallValueThreshold = 0.001; // Track changes as small as 0.001 units
    const needsUpdate = this.resourceUpdateNeeded || 
                      Math.abs(this.accumulatedATP) > smallValueThreshold || 
                      Math.abs(this.accumulatedGlucose) > smallValueThreshold;
    
    // Log detailed production data occasionally for debugging
    if ((Math.abs(this.naturalATPRegeneration) > 0.0001 || 
        Math.abs(this.mitochondriaATPProduction) > 0.0001) && 
        Math.random() < 0.1) {
      console.log(`Resource production details: Natural ATP: +${this.naturalATPRegeneration.toFixed(4)}, Mitochondria ATP: +${this.mitochondriaATPProduction.toFixed(4)}, Glucose: -${this.mitochondriaGlucoseConsumption.toFixed(4)}`);
    }

    // Build the result object with source-specific details
    return {
      total: {
        atp: this.accumulatedATP,
        glucose: this.accumulatedGlucose
      },
      sources: {
        natural: { 
          atp: this.naturalATPRegeneration 
        },
        mitochondria: {
          atp: this.mitochondriaATPProduction,
          glucose: this.mitochondriaGlucoseConsumption,
          count: this.getOrganellesByType('mitochondrion').length
        }
      },
      needsUpdate
    };
  }

  /**
   * Returns accumulated resource production since last call.
   * This is used to sync with the server and reset tracking.
   */
  public getAndResetResourceProduction(): { atp: number, glucose: number, needsUpdate: boolean } {
    // Always set needsUpdate to true even for small values
    // This ensures regular updates to the server for natural regeneration
    const smallValueThreshold = 0.001; // Track changes as small as 0.001 units
    
    // Return the accumulated values with higher precision
    const result = {
      atp: this.accumulatedATP,
      glucose: this.accumulatedGlucose,
      // Force update if accumulated values exceed our small threshold or if explicitly needed
      needsUpdate: this.resourceUpdateNeeded || 
                  Math.abs(this.accumulatedATP) > smallValueThreshold || 
                  Math.abs(this.accumulatedGlucose) > smallValueThreshold
    };
    
    // Log the accumulated values for debugging, but don't require a high threshold
    if (Math.abs(this.accumulatedATP) > 0.0001 || Math.abs(this.accumulatedGlucose) > 0.0001) {
      console.log(`Resource production since last reset: ATP ${this.accumulatedATP > 0 ? '+' : ''}${this.accumulatedATP.toFixed(4)}, glucose ${this.accumulatedGlucose > 0 ? '+' : ''}${this.accumulatedGlucose.toFixed(4)}`);
    }
    
    // Reset the accumulators and update flag
    this.accumulatedATP = 0;
    this.accumulatedGlucose = 0;
    this.naturalATPRegeneration = 0;
    this.mitochondriaATPProduction = 0;
    this.mitochondriaGlucoseConsumption = 0;
    this.resourceUpdateNeeded = false;
    
    return result;
  }

  /**
   * Update maximum ATP based on number of mitochondria and membrane stage
   */
  private updateMaxATP(): void {
    const mitochondria = this.getOrganellesByType('mitochondrion');
    const newMaxATP = calculateMaxATP(this.membraneStage, mitochondria.length);
    
    // Only log and update if the value actually changed
    if (this.maxATP !== newMaxATP) {
      // Log before updating to see previous value
      console.log(`Updating max ATP: ${this.maxATP} → ${newMaxATP} (stage: ${this.membraneStage}, mitochondria: ${mitochondria.length})`);
      this.maxATP = newMaxATP;
      
      // If we're above max capacity after recalculation, cap ATP at new max
      if (this.atp > this.maxATP) {
        this.atp = this.maxATP;
        console.log(`Capped ATP at new maximum: ${this.maxATP}`);
      }
    }
  }
  
  /**
   * Get the current maximum ATP capacity
   */
  public getMaxATP(): number {
    return this.maxATP;
  }
  
  /**
   * Get current glucose level
   */
  public getGlucose(): number {
    return this.glucose;
  }
  
  /**
   * Set glucose level
   */
  public setGlucose(value: number): void {
    this.glucose = Math.max(0, value);
  }

  /**
   * Get all organelles of a specific type
   * @param type The type of organelle to find
   * @returns Array of organelles matching the type
   */
  public getOrganellesByType(type: string): Organelle[] {
    return this.organelles.filter(organelle => organelle.getType() === type);
  }

  /**
   * Updates all mitochondria with current resource information
   */
  private updateMitochondriaResources(): void {
    const mitochondria = this.getOrganellesByType('mitochondrion');
    
    for (const mito of mitochondria) {
      if (mito.getType() === 'mitochondrion' && 
          typeof (mito as any).updateResourceInfo === 'function') {
        (mito as any).updateResourceInfo({
          cellGlucose: this.glucose,
          cellATP: this.atp,
          cellMaxATP: this.maxATP
        });
      }
    }
  }
  
  /**
   * Properly clean up all resources, event listeners, and timers when the cell is destroyed
   * This is essential to prevent memory leaks and "this.scene is undefined" errors
   */
  destroy(fromScene?: boolean): void {
    // Remove event listeners
    if (this.scene) {
      // Remove resize listener
      this.scene.scale.off('resize', this.handleResize, this);
      
      // Remove input listeners - critical for preventing "this.scene is undefined" errors
      this.scene.input.off('pointerdown', this.setupClickToMove);
      
      // Log cleanup
      console.log(`Destroying cell with ${this.organelles.length} organelles`);
    }
    
    // Clear any active timers
    if (this.returnToCenterTimer) {
      this.returnToCenterTimer.remove();
      this.returnToCenterTimer = null;
    }
    
    // Destroy all child organelles
    [...this.organelles].forEach(organelle => {
      this.removeOrganelle(organelle);
    });
    
    // Reset collections
    this.organelles = [];
    this.mitochondria = [];
    this.lysosomes = [];
    this.chloroplasts = [];
    this.flagella = [];
    
    // Destroy low ATP warning if it exists
    if (this.lowATPWarningText) {
      this.lowATPWarningText.destroy();
      this.lowATPWarningText = null;
    }
    
    // Finally call the parent destroy method
    super.destroy(fromScene);
  }

  public getPlayerID(): string {
    return this.playerID;
  }
  
  public setPlayerID(id: string): void {
    this.playerID = id;
  }
} 